// The mirror is an inert document, never a second logged-in application. Both
// capture and display sanitize; the iframe also forbids scripts, forms and all
// network requests. Password/private fields never leave the source browser.
export const MIRROR_CSP = "default-src 'none'; script-src 'none'; connect-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'";
const PRIVATE = '[data-mirror-private], input[type="password"], input[type="hidden"], input[type="file"], [autocomplete*="password"], [autocomplete="one-time-code"], [name*="password" i], [name*="token" i], [name*="secret" i]';
const FORBIDDEN = 'script,iframe,object,embed,base,link,meta,video,audio,source,track,template,animate,animateMotion,animateTransform,set,input[type="hidden"]';
const safeCss = (css) => css.replace(/@import\s[^;]+;/gi, '').replace(/url\([^)]*\)/gi, 'none');
const visualStates = ['hover', 'focus-visible', 'focus-within', 'focus'];
// Copy visual/layout properties only. Serializing all ~450 computed properties
// per element turns an ordinary customer form into a >2 MB document whenever
// an external font stylesheet is present, exceeding the bounded transport.
const visualProperties = [
  'display', 'visibility', 'opacity', 'color', 'background-color',
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
  'text-align', 'text-transform', 'text-decoration', 'text-indent', 'letter-spacing', 'white-space', 'word-break',
  'box-sizing', 'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height',
  'padding', 'margin', 'border', 'border-radius', 'box-shadow', 'outline',
  'overflow-x', 'overflow-y', 'position', 'top', 'right', 'bottom', 'left', 'z-index',
  'float', 'clear', 'vertical-align', 'transform', 'transform-origin',
  'flex-direction', 'flex-wrap', 'flex-grow', 'flex-shrink', 'flex-basis', 'order',
  'align-items', 'align-self', 'align-content', 'justify-content', 'justify-self', 'gap',
  'grid-template-columns', 'grid-template-rows', 'grid-column', 'grid-row',
  'border-collapse', 'border-spacing', 'table-layout', 'list-style-type',
];

export const sanitizeMirrorDocument = (doc) => {
  doc.querySelectorAll(FORBIDDEN).forEach((node) => node.remove());
  doc.querySelectorAll(PRIVATE).forEach((node) => {
    const placeholder = doc.createElement('span');
    placeholder.textContent = '[Private]';
    placeholder.setAttribute('style', node.getAttribute('style') || 'display:inline-block');
    node.replaceWith(placeholder);
  });
  doc.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach(({ name, value }) => {
      if (/^on/i.test(name) || /^(href|xlink:href|srcdoc|srcset|action|formaction|nonce|integrity|autofocus|contenteditable|ping)$/i.test(name)
        || (name.startsWith('data-') && name !== 'data-mirror-scroll')
        || (name === 'src' && !/^data:image\/(png|jpeg|webp|gif);base64,/i.test(value))) node.removeAttribute(name);
    });
    if (node.hasAttribute('style')) node.setAttribute('style', safeCss(node.getAttribute('style')));
    if (node.tagName === 'STYLE') node.textContent = safeCss(node.textContent);
  });
  const policy = doc.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  policy.content = MIRROR_CSP;
  doc.head.prepend(policy);
  const inertStyle = doc.createElement('style');
  inertStyle.textContent = 'html{scroll-behavior:auto!important}*,*::before,*::after{animation:none!important;transition:none!important;caret-color:transparent!important}body{pointer-events:none!important}';
  doc.head.appendChild(inertStyle);
  return doc;
};

export const safeMirrorHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitizeMirrorDocument(doc);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
};

export const captureMirrorDocument = (source = document, pointer = null, clicks = []) => {
  const cloned = source.documentElement.cloneNode(true);
  const doc = source.implementation.createHTMLDocument('Tempus support mirror');
  doc.replaceChild(cloned, doc.documentElement);
  const originals = source.querySelectorAll('*');
  const copies = doc.querySelectorAll('*');
  let missingStyles = false;
  const css = [...source.styleSheets].map((sheet) => {
    try { return [...sheet.cssRules].map((rule) => rule.cssText).join('\n'); }
    catch (_) { missingStyles = true; return ''; }
  }).join('\n').replace(/:(hover|focus-visible|focus-within|focus)\b/g, '.tempus-mirror-$1');
  // CSSOM is essential: styled-components production style tags may have no
  // textContent. Cross-origin sheets cannot be read; computed styles retain
  // their visual result without making the viewer fetch those URLs.
  originals.forEach((original, index) => {
    const copy = copies[index];
    if (!copy) return;
    if (original.matches(PRIVATE)) {
      const bounds = original.getBoundingClientRect();
      copy.style.cssText = `display:inline-block;box-sizing:border-box;width:${bounds.width}px;height:${bounds.height}px;overflow:hidden;`;
      return;
    }
    if (original.closest(PRIVATE)) return;
    visualStates.forEach((state) => {
      try { if (original.matches(`:${state}`)) copy.classList.add(`tempus-mirror-${state}`); } catch (_) { /* Older browsers may not implement focus-visible. */ }
    });
    if (missingStyles && source.defaultView && original.tagName !== 'STYLE') {
      const computed = source.defaultView.getComputedStyle(original);
      const styles = visualProperties.map((key) => [key, computed.getPropertyValue(key)]).filter(([, value]) => value);
      copy.setAttribute('style', styles.map(([key, value]) => `${key}:${value}`).join(';'));
    }
    if (original.tagName === 'INPUT') {
      copy.setAttribute('value', original.value);
      if (original.checked) copy.setAttribute('checked', ''); else copy.removeAttribute('checked');
    }
    if (original.tagName === 'TEXTAREA') copy.textContent = original.value;
    if (original.tagName === 'OPTION') {
      if (original.selected) copy.setAttribute('selected', ''); else copy.removeAttribute('selected');
    }
    if (original.scrollTop || original.scrollLeft) copy.setAttribute('data-mirror-scroll', JSON.stringify([original.scrollLeft, original.scrollTop]));
    if (original.tagName === 'CANVAS' || (original.tagName === 'IMG' && original.complete && original.naturalWidth)) {
      try {
        const canvas = original.tagName === 'CANVAS' ? original : source.createElement('canvas');
        if (original.tagName === 'IMG') {
          canvas.width = original.naturalWidth; canvas.height = original.naturalHeight;
          canvas.getContext('2d').drawImage(original, 0, 0);
        }
        const image = doc.createElement('img');
        [...copy.attributes].forEach(({ name, value }) => image.setAttribute(name, value));
        image.setAttribute('src', canvas.toDataURL('image/png'));
        copy.replaceWith(image);
      } catch (_) { /* Cross-origin images remain placeholders, never refetched. */ }
    }
  });
  doc.querySelectorAll('style').forEach((node) => node.remove());
  const styles = doc.createElement('style'); styles.textContent = css; doc.head.appendChild(styles);
  sanitizeMirrorDocument(doc);
  return {
    html: `<!doctype html>${doc.documentElement.outerHTML}`,
    width: window.innerWidth, height: window.innerHeight,
    scrollX: window.scrollX, scrollY: window.scrollY,
    pointer, clicks, path: window.location.pathname,
  };
};

export const restoreMirrorScroll = (iframe, frame) => {
  const doc = iframe?.contentDocument;
  if (!doc) return;
  doc.querySelectorAll('[data-mirror-scroll]').forEach((node) => {
    try {
      const [x, y] = JSON.parse(node.getAttribute('data-mirror-scroll'));
      if (Number.isFinite(x) && Number.isFinite(y)) { node.scrollLeft = x; node.scrollTop = y; }
    } catch (_) { /* Untrusted malformed scroll coordinates have no effect. */ }
  });
  iframe.contentWindow.scrollTo(Number(frame.scrollX) || 0, Number(frame.scrollY) || 0);
};
